import * as Api from '@ViewModels';
import {
	ConfigurableIntegrationType,
	IAms360Integration,
	IConfigurableIntegration,
	IContact,
	IHawkSoftIntegration,
	IOperationResultNoValue,
	ResourceAutoCompleteModel,
	ResourceAutoCompleteViewModel,
	ResourceAutoCompleteViewModelType,
} from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer, Provider } from 'mobx-react';
import * as React from 'react';
import { Suspense } from 'react';
import { NavLink, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps } from '../../../../../models';
import { IAdminUserSessionComponentProps, MastheadPortalId } from '../../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../../models/Logging';
import { useAdminAccountQuery } from '../../../../../queries/Admin/Account/useAdminAccountQuery';
import {
	AdminPermission,
	AdminUserSessionContext,
	ImpersonationContextViewModel,
} from '../../../../../viewmodels/AdminViewModels';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../../../web/components/autocomplete/SimpleAutoCompleteSearchField';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../../../web/components/Breadcrumb';
import { Button } from '../../../../../web/components/Button';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { ISelectOption } from '../../../../../web/components/DeprecatedSelect';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../../web/components/Modal';
import { MultiContainerHeader } from '../../../../../web/components/MultiContainerHeader';
import { RadioButton } from '../../../../../web/components/RadioButton';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { AdminConfigurableIntegrationSettings } from '../../../../components/integrations/AdminConfigurableIntegrationSettings';
import { IntegrationsManageUsersTable } from '../../../../components/integrations/IntegrationsManageUsersTable';
import { AdminConfigurableIntegrationViewModel } from '../../../../viewModels';
import { ConfigurableIntegrationSettingsAdminRecentActivity } from './presentation';
import { styleSheet } from './styles';

interface IProps extends IAdminUserSessionComponentProps, IErrorMessageComponentProps, IEventLoggingComponentProps {
	className?: string;
	integrationType: ConfigurableIntegrationType;
	styles?: StyleDeclarationValue[];
	accountId: string;
	accountName?: string;
}

interface IState {
	integration?: IConfigurableIntegration;
	isLoading?: boolean;
	reloadOptions?: IReloadOption[];
	resyncing?: boolean;
	selectedResyncOption?: ISelectOption<ResyncType>;
	selectedContactForReload?: string;
	showResyncModal?: boolean;
	showMapByResourceIdModal?: boolean;
	canUpdateMappingKey?: boolean;
}

enum ResyncType {
	Full = 'Full',
	Local = 'Local',
	Reload = 'Reload',
}

enum ReloadOption {
	EmailAddresses = 'Resource.EmailAddresses',
	FirstName = 'Resource.FirstName',
	KeyFacts = 'Resource.KeyFacts',
	LastName = 'Resource.LastName',
	NickName = 'Resource.NickName',
	PhoneNumbers = 'Resource.PhoneNumbers',
	Tags = 'Resource.Tags',
	IntegrationOwnerIds = 'Resource.IntegrationOwnerIds',
}

interface IReloadOption {
	checked: boolean;
	text: string;
	value: ReloadOption;
}

const resyncOptions: ISelectOption<ResyncType>[] = [
	{
		dataContext: ResyncType.Local,
		id: 'resync-option-local-resync-only',
		text: 'Local Resync Only',
	},
	{
		dataContext: ResyncType.Full,
		id: 'resync-option-full-resync',
		text: 'Full Resync',
	},
	{
		dataContext: ResyncType.Reload,
		id: 'resync-option-reload',
		text: 'Reload ',
	},
];

const confirmMappingKeyModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Update',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const CONFIGURABLE_INTEGRATION_TYPE_NAME_MAP: Partial<Record<ConfigurableIntegrationType, string>> = {
	[ConfigurableIntegrationType.Ams360]: 'AMS360',
	[ConfigurableIntegrationType.HawkSoft]: 'HawkSoft',
	[ConfigurableIntegrationType.Eclipse]: 'Eclipse',
	[ConfigurableIntegrationType.EzLynx]: 'EzLynx',
	[ConfigurableIntegrationType.Xanatek]: 'Xanatek',
	[ConfigurableIntegrationType.Epic]: 'Epic',
	[ConfigurableIntegrationType.Redtail]: 'Redtail',
	[ConfigurableIntegrationType.Clio]: 'Clio',
	[ConfigurableIntegrationType.QQ]: 'QQCatalyst',
	[ConfigurableIntegrationType.HubSpot]: 'HubSpot',
	[ConfigurableIntegrationType.DonorPerfect]: 'DonorPerfect',
};

export class _ConfigurableIntegrationSettingsAdmin extends React.Component<IProps, IState> {
	integrationName = '';
	private mIntegrationVm: AdminConfigurableIntegrationViewModel;
	private mImpersonationProviderProps: IImpersonationContextComponentProps = {
		impersonationContext: new ImpersonationContextViewModel(),
	};

	constructor(props: IProps) {
		super(props);

		makeObservable<_ConfigurableIntegrationSettingsAdmin, 'mIntegrationVm'>(this, {
			mIntegrationVm: observable.ref,
		});
		this.mIntegrationVm = new AdminConfigurableIntegrationViewModel(
			props.userSession as AdminUserSessionContext,
			{ id: props.accountId },
			props.integrationType
		);

		this.state = {
			integration: undefined,
			isLoading: false,
		};
		this.integrationName = CONFIGURABLE_INTEGRATION_TYPE_NAME_MAP[props.integrationType];
	}

	public componentDidMount() {
		const { errorMessages, logApiError } = this.props;

		if (this.mIntegrationVm) {
			this.mIntegrationVm
				.load()
				.then(() => {
					this.setState({
						integration: this.mIntegrationVm.toJs(),
						canUpdateMappingKey:
							this.mIntegrationVm.toJs().preferredMappingKey === Api.IntegrationPreferredMappingKey.Email,
					});

					this.mImpersonationProviderProps.impersonationContext.update({
						account: this.mIntegrationVm.account,
					});
				})
				.catch(e => {
					logApiError('GetIntegration-Error', e);

					errorMessages.push({
						messages: ['Could not load the account integration'],
					});
				});
		}
		this.setIntegrationResyncToDefault();
	}

	public render() {
		const { className, integrationType, styles, userSession } = this.props;
		const { isLoading, showResyncModal, integration, showMapByResourceIdModal, canUpdateMappingKey } = this.state;

		return (
			<Provider {...this.mImpersonationProviderProps}>
				<div className={`${css(...(styles || []))} configurable-integration-settings-admin ${className || ''}`}>
					<MultiContainerHeader
						appBarHeader={
							<Breadcrumb>
								<BreadcrumbList>
									<BreadcrumbItem>
										<BreadcrumbLink asChild>
											<NavLink to='/accounts'>Accounts</NavLink>
										</BreadcrumbLink>
									</BreadcrumbItem>
									<BreadcrumbSeparator />
									<BreadcrumbItem>
										<BreadcrumbLink asChild>
											<NavLink to={`/accounts/${this.props.accountId}?tab=Integrations`}>
												{this.props.accountName ?? 'Loading...'}
											</NavLink>
										</BreadcrumbLink>
									</BreadcrumbItem>
									<BreadcrumbSeparator />
									<BreadcrumbItem>
										<BreadcrumbPage>{this.integrationName}</BreadcrumbPage>
									</BreadcrumbItem>
								</BreadcrumbList>
							</Breadcrumb>
						}
						appBarPortalDestinationId={MastheadPortalId}
					/>
					{!!integration && (
						<AdminConfigurableIntegrationSettings
							accountId={this.mIntegrationVm.account.id}
							integration={integration}
							integrationName={this.integrationName}
							integrationType={integrationType}
							isLoading={isLoading}
							onRenderMapping={this.onRenderMapping}
							onRenderRecentActivity={
								userSession.hasPermission(AdminPermission.ModifyAccount) ? this.onRenderRecentActivity : undefined
							}
							onRequestRecentActivityReload={
								!this.mIntegrationVm.isLoadingRecentActivity && this.onRequestRecentActivityReload
							}
							onResyncLocal={this.onResyncClick}
							onSignIn={this.onSignIn}
							onUnmergeContacts={this.onUnmergeContacts}
							onUpdateIntegrationSettings={this.onUpdateIntegrationSettings}
							onMapByResourceId={this.onUpdateMappingKeyClick}
							canUpdateMappingKey={canUpdateMappingKey}
						/>
					)}
					<Modal isOpen={showResyncModal} onRequestClose={this.onResyncModalClose} useDefaultHeader={true}>
						<div className={css(styleSheet.resyncModal)}>
							<div className={css(styleSheet.resyncHeader)}>Integration Resync</div>
							<div className={css(styleSheet.resyncContainer)}>
								{Api.isReportBasedIntegration(integrationType)
									? this.renderReportIntegrationResyncModalOptions()
									: this.renderResyncModalOptions()}
								{this.renderResyncModalBody()}
							</div>
						</div>
					</Modal>
					<ConfirmationDialog
						icon={<WarningIcon />}
						modalProps={{
							isOpen: showMapByResourceIdModal,
							onRequestClose: this.onConfirmUpdateMappingKey,
						}}
						options={confirmMappingKeyModalOptions}
						title='You are updating the unique identifier for contacts to be the source system ID instead of email address.  Click Update to set the unique identifier, unmerge contacts, and reload the data on the account.'
					/>
				</div>
			</Provider>
		);
	}

	private onConfirmUpdateMappingKey = async (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		const { integration } = this.state;
		if (!!result?.isDestructive && !canceled) {
			try {
				await this.mIntegrationVm.setPreferredMappingKeyToResourceId();

				integration.preferredMappingKey = Api.IntegrationPreferredMappingKey.ResourceId;

				toast.success(
					'Your contacts will now be mapped by Resource ID.  An unmerge of your contacts has been scheduled to update the data.'
				);

				this.setState({
					integration,
					canUpdateMappingKey: false,
				});
			} catch (e) {
				const { errorMessages, logApiError } = this.props;
				errorMessages.pushApiError(e);
				logApiError('UpdateMappingKeyToResourceId-Error', e);
			}
		}

		this.setState({
			showMapByResourceIdModal: false,
		});
	};

	private onRenderRecentActivity = () => {
		return <ConfigurableIntegrationSettingsAdminRecentActivity integrationSettings={this.mIntegrationVm} />;
	};

	private onRequestRecentActivityReload = () => {
		return this.mIntegrationVm.loadRecentActivity();
	};

	private execReload = () => {
		const { reloadOptions = [], selectedContactForReload } = this.state;
		this.setState({ resyncing: true }, () => {
			this.mIntegrationVm
				.reload(
					selectedContactForReload,
					reloadOptions.filter(option => option.checked).map(option => option.value)
				)
				?.then(() => {
					toast.success(
						`Levitate will start reloading this ${
							selectedContactForReload ? 'user' : 'integration'
						}. This may take a few minutes.`
					);
				})
				?.catch((err: IOperationResultNoValue) => {
					const { errorMessages, logApiError } = this.props;

					errorMessages.pushApiError(err);

					logApiError('ReloadIntegration-Error', err);
				})
				?.finally(() => {
					this.setIntegrationResyncToDefault();
				});
		});
	};

	private execResync = (isFullReload: boolean) => () => {
		this.setState({ resyncing: true }, () => {
			this.mIntegrationVm
				.resync(isFullReload)
				?.then(() => {
					toast.success('Levitate will start resyncing with this integration. This may take a few minutes.');
				})
				?.catch((err: IOperationResultNoValue) => {
					const { errorMessages, logApiError } = this.props;

					errorMessages.pushApiError(err);

					logApiError(`ResyncIntegration${isFullReload ? '' : 'Local'}-Error`, err);
				})
				?.finally(() => {
					this.setIntegrationResyncToDefault();
				});
		});
	};

	private onUnmergeContacts = async () => {
		try {
			await this.mIntegrationVm.getUnmergeContacts();
			toast.success('Your request to unmerge contacts has been sent. Check back after contact reload.');
		} catch (e) {
			const { errorMessages, logApiError } = this.props;

			errorMessages.pushApiError(e);

			logApiError('ContactUnmerge-Error', e);
		}
	};

	private onCreateReloadAutoCompleteViewModel = (
		_: ResourceAutoCompleteViewModelType,
		suggestedViewModel: ResourceAutoCompleteViewModel<ResourceAutoCompleteModel>
	) => {
		suggestedViewModel.impersonate({
			account: this.mIntegrationVm.account,
		});
		return suggestedViewModel;
	};

	private onReloadContactSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<IContact>) => {
		this.setState({ selectedContactForReload: e.selection.id });
	};

	private onReloadOptionClick = (option: IReloadOption) => () => {
		const { reloadOptions } = this.state;

		// option is being unchecked, need to check if all options are now unchecked.
		// if so, need to re-check them all (no empty reload options state allowed)
		const resetNeeded =
			(option.checked ? reloadOptions.filter(o => o.value !== option.value && o.checked) : [false]).length === 0;

		const updatedOptions = reloadOptions.map(o => {
			if (resetNeeded) {
				o.checked = true;
			} else if (o.value === option.value) {
				o.checked = !o.checked;
			}

			return o;
		});

		this.setState({ reloadOptions: updatedOptions });
	};

	private onRenderMapping = () => {
		if (this.mIntegrationVm?.configured) {
			return (
				<Suspense fallback={<LoadingSpinner type='large' />}>
					<IntegrationsManageUsersTable
						accountId={this.mIntegrationVm.account.id}
						integrationType={this.props.integrationType}
					/>
				</Suspense>
			);
		}
	};

	private onResyncAutoCompleteCleared = () => {
		this.setState({ selectedContactForReload: null });
	};

	private renderResyncModalBody() {
		const { resyncing, reloadOptions = [], selectedResyncOption } = this.state;

		let content: JSX.Element = null;

		switch (selectedResyncOption?.dataContext) {
			case ResyncType.Full:
				content = (
					<>
						<p>Calls out to the provider and resyncs the entire account.</p>
						<Button className='resync-cta' label='Execute Full Resync' onClick={this.execResync(true)} />
					</>
				);
				break;
			case ResyncType.Reload:
				content = (
					<>
						<p>Forcefully reload particular fields for a specific contact.</p>
						<p className={`warning ${css(styleSheet.warningText)}`}>If left empty, all contacts will be reloaded.</p>
						<SimpleAutoCompleteSearchField
							onClear={this.onResyncAutoCompleteCleared}
							onCreateAutoCompleteViewModel={this.onCreateReloadAutoCompleteViewModel}
							// @ts-ignore
							onItemSelected={this.onReloadContactSelected}
							resultsLimit={3}
							pageSize={3}
							placeholder='Search contacts from integration'
							style={styleSheet.reloadAutoComplete}
							type={ResourceAutoCompleteViewModelType.Contact}
						/>
						<div className={css(styleSheet.reloadOptions)}>
							{reloadOptions.map(option => (
								<div key={option.value}>
									<Checkbox
										id={`reload-option-${option.value}`}
										type='large'
										onChange={this.onReloadOptionClick(option)}
										checked={option.checked}
									>
										<span className={css(styleSheet.checkboxContent)}>{option.text}</span>
									</Checkbox>
								</div>
							))}
						</div>
						<Button className='resync-cta' label='Execute Reload' onClick={this.execReload} />
					</>
				);
				break;
			default:
				content = (
					<>
						<p>
							Does not call out to the provider, but only checks for differences between the current integration
							collection and Levitate.
						</p>
						<Button className='resync-cta' label='Execute Local Resync' onClick={this.execResync(false)} />
					</>
				);
				break;
		}

		return (
			<div className={css(styleSheet.resyncBody)}>
				{content}
				{resyncing && <LoadingSpinner type='small' className={css(baseStyleSheet.absoluteCenter)} />}
			</div>
		);
	}

	private renderResyncModalOptions() {
		return this.getResyncModalOptions(resyncOptions);
	}

	private renderReportIntegrationResyncModalOptions() {
		return this.getResyncModalOptions(
			resyncOptions.filter(function (option) {
				return option.dataContext !== ResyncType.Full;
			})
		);
	}

	private getResyncModalOptions(resyncModalOptions: ISelectOption<ResyncType>[]) {
		const { selectedResyncOption } = this.state;

		return (
			<div>
				{resyncModalOptions.map(option => (
					<RadioButton
						className={css(styleSheet.radio)}
						checked={option.dataContext === selectedResyncOption?.dataContext}
						key={option.dataContext}
						id={`resync-option-${option.id}`}
						name='resync-options'
						onClick={this.onResyncOptionChange(option)}
						readOnly={true}
					>
						<div className={css(styleSheet.radioText)}>{option.text}</div>
					</RadioButton>
				))}
			</div>
		);
	}

	private setIntegrationResyncToDefault = () => {
		this.setState({
			reloadOptions: [
				{
					checked: false,
					text: 'First Name',
					value: ReloadOption.FirstName,
				},
				{
					checked: false,
					text: 'Last Name',
					value: ReloadOption.LastName,
				},
				{
					checked: false,
					text: 'Nickname',
					value: ReloadOption.NickName,
				},
				{
					checked: true,
					text: 'Phone Numbers',
					value: ReloadOption.PhoneNumbers,
				},
				{
					checked: true,
					text: 'Email Addresses',
					value: ReloadOption.EmailAddresses,
				},
				{
					checked: true,
					text: 'Tags',
					value: ReloadOption.Tags,
				},
				{
					checked: true,
					text: 'Key Facts',
					value: ReloadOption.KeyFacts,
				},
				{
					checked: true,
					text: 'Owners',
					value: ReloadOption.IntegrationOwnerIds,
				},
			],
			resyncing: false,

			selectedContactForReload: null,
			selectedResyncOption: resyncOptions[0],
			showResyncModal: false,
		});
	};

	private onResyncModalClose = () => {
		this.setState({ showResyncModal: false });
	};

	private onResyncOptionChange = (option: ISelectOption<ResyncType>) => () => {
		this.setState({ selectedResyncOption: option });
	};

	private onSignIn = (integration: Partial<IConfigurableIntegration>) => {
		const { errorMessages, logApiError } = this.props;
		this.setState({ isLoading: true });
		this.mIntegrationVm
			.configure(integration)
			.then(() => {
				this.setState({
					integration: this.mIntegrationVm.toJs(),
					isLoading: false,
					canUpdateMappingKey:
						this.mIntegrationVm.toJs().preferredMappingKey === Api.IntegrationPreferredMappingKey.Email,
				});
			})
			.catch((error: IOperationResultNoValue) => {
				this.setState({ isLoading: false });

				logApiError('ConfigureIntegration-Error', error);

				errorMessages.pushApiError(error);
			});
	};

	private pathComponentNameProvider = (companyName?: string) => (pathComponent: string) => {
		if (pathComponent === 'integration') {
			return null;
		}
		if (pathComponent === this.mIntegrationVm?.account.id) {
			return companyName || 'Loading company...';
		}
		return pathComponent
			? `${pathComponent.charAt(0).toLocaleUpperCase()}${pathComponent.substring(1)}`
			: pathComponent;
	};

	private onUpdateIntegrationSettings = (
		fieldsToUpdate: Partial<IConfigurableIntegration & IAms360Integration & IHawkSoftIntegration>
	) => {
		this.setState({ isLoading: true }, () => {
			this.mIntegrationVm
				.configure(fieldsToUpdate)
				?.then(() => {
					this.setState({
						integration: this.mIntegrationVm.toJs(),
						isLoading: false,
						canUpdateMappingKey:
							this.mIntegrationVm.toJs().preferredMappingKey === Api.IntegrationPreferredMappingKey.Email,
					});
				})
				?.catch(e => {
					const { errorMessages, logApiError } = this.props;
					this.setState({ isLoading: false });

					logApiError('ConfigureIntegrationSettings-Error', e);

					errorMessages.push({
						messages: ['Could not update integration settings'],
					});
				});
		});
	};

	private onResyncClick = () => {
		const { userSession } = this.props;
		if (
			(userSession as AdminUserSessionContext).hasPermission(AdminPermission.SupportLevel3) ||
			(userSession as AdminUserSessionContext).hasPermission(AdminPermission.OnlyForDevelopers) ||
			(userSession as AdminUserSessionContext).hasPermission(AdminPermission.ResetAccount)
		) {
			this.setState({
				selectedResyncOption: resyncOptions.find(x => x.dataContext === ResyncType.Local),
				showResyncModal: true,
			});
		} else {
			this.execResync(false);
		}
	};

	private onUpdateMappingKeyClick = () => {
		this.setState({
			showMapByResourceIdModal: true,
		});
	};
}

const ConfigurableIntegrationSettingsAdminAsObserver = observer(_ConfigurableIntegrationSettingsAdmin);
const ConfigurableIntegrationSettingsAdminWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey,
	ErrorMessagesViewModelKey
)((props: IProps) => {
	const integrationName = CONFIGURABLE_INTEGRATION_TYPE_NAME_MAP[props.integrationType];
	const WrappedComponent = React.useRef(
		withEventLogging(
			ConfigurableIntegrationSettingsAdminAsObserver,
			`${integrationName.replace(' ', '')}ConfigurableIntegrationSettingsAdmin`
		)
	).current;
	return <WrappedComponent {...props} />;
});
export const ConfigurableIntegrationSettingsAdmin = (props: Omit<IProps, 'integrationType' | 'accountId'>) => {
	const params = useParams<{ id: string }>();
	const routeMatch = useRouteMatch<{ integrationType: ConfigurableIntegrationType }>();
	const accountQuery = useAdminAccountQuery({ accountId: params.id });
	return (
		<ConfigurableIntegrationSettingsAdminWithContext
			{...props}
			integrationType={routeMatch.params.integrationType}
			accountId={params.id}
			accountName={accountQuery.data?.companyName}
		/>
	);
};
